import {useEffect, useMemo, useRef, type KeyboardEvent} from 'react'

import styles from './PlanTypeSegmentedControl.module.css'

export const PLAN_TYPES = {
  Individual: 'individual',
  Business: 'business',
} as const

export type PlanType = (typeof PLAN_TYPES)[keyof typeof PLAN_TYPES]

type Props = {
  value: PlanType
  onChange: (value: PlanType) => void
  heading: string
  items: Array<{
    id: string
    value: PlanType
    label: string
  }>
}

export function PlanTypeSegmentedControl(props: Props) {
  const {value, onChange, heading, items} = props

  const tabsRef = useRef<Array<HTMLButtonElement | null>>([])
  const isUserInitiatedFocus = useRef<boolean>(false)

  const currentIndex = useMemo(() => items.findIndex(item => item.value === value), [value, items])

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    let newIndex: number | null = null

    switch (event.code) {
      case 'ArrowLeft':
        newIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1
        break
      case 'ArrowRight':
        newIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1
        break
      case 'Home':
        newIndex = 0
        break
      case 'End':
        newIndex = items.length - 1
        break
      default:
        return
    }

    if (newIndex !== null && newIndex !== currentIndex) {
      if (items[newIndex]) {
        isUserInitiatedFocus.current = true
        onChange(items[newIndex]!.value)
      }

      event.preventDefault()
    }
  }

  useEffect(() => {
    if (isUserInitiatedFocus.current) {
      tabsRef.current[currentIndex]?.focus()
      isUserInitiatedFocus.current = false
    }
  }, [currentIndex])

  return (
    <div className={styles.container} role="tablist" aria-label={heading || 'Pricing plans'}>
      {items.map((item, index) => (
        <button
          key={item.id}
          className={styles.segment}
          id={item.id}
          onClick={() => onChange(item.value)}
          onKeyDown={handleKeyDown}
          role="tab"
          aria-selected={value === item.value}
          aria-controls={`${item.id}-panel`}
          tabIndex={value === item.value ? 0 : -1}
          ref={el => {
            tabsRef.current[index] = el
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}
